import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, Bell, Building2, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function WorkerDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const quickStats = [
    { label: 'I Miei Rapportini', value: stats?.my_rapportini || 0, icon: ClipboardList, color: 'bg-blue-100 text-blue-600' },
    { label: 'In Attesa', value: stats?.pending_rapportini || 0, icon: Clock, color: 'bg-amber-100 text-amber-600' },
    { label: 'Approvati', value: stats?.approved_rapportini || 0, icon: CheckCircle, color: 'bg-emerald-100 text-emerald-600' },
    { label: 'Notifiche', value: stats?.unread_notifications || 0, icon: Bell, color: 'bg-red-100 text-red-600' },
  ];

  return (
    <div className="space-y-6" data-testid="worker-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-500 text-sm">Benvenuto nella tua area di lavoro</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        {quickStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${stat.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Assigned Cantieri */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-3">I Tuoi Cantieri</h2>
        {stats?.assigned_cantieri?.length > 0 ? (
          <div className="space-y-3">
            {stats.assigned_cantieri.map((cantiere) => (
              <Card key={cantiere.id} className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Building2 className="h-5 w-5 text-slate-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-slate-900">{cantiere.name}</h3>
                        <Badge 
                          variant="outline" 
                          className={cantiere.status === 'attivo' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'}
                        >
                          {cantiere.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{cantiere.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="py-8 text-center">
              <Building2 className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Nessun cantiere assegnato</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-3">Azioni Rapide</h2>
        <div className="grid grid-cols-2 gap-3">
          <button 
            onClick={() => navigate('/worker/rapportini/nuovo')}
            className="p-4 bg-slate-900 text-white rounded-lg flex flex-col items-center gap-2 active:scale-95 transition-transform"
            data-testid="new-rapportino-btn"
          >
            <ClipboardList className="h-6 w-6" />
            <span className="text-sm font-medium">Nuovo Rapportino</span>
          </button>
          <button 
            onClick={() => navigate('/worker/documenti')}
            className="p-4 bg-white border border-slate-200 text-slate-900 rounded-lg flex flex-col items-center gap-2 active:scale-95 transition-transform"
            data-testid="view-documents-btn"
          >
            <Bell className="h-6 w-6" />
            <span className="text-sm font-medium">Documenti</span>
          </button>
        </div>
      </div>
    </div>
  );
}
